import type { KarteUser, Karte, Sex, Child } from '@/types/app'
import { convertLegacyChannelToGqlType } from './channel'
import type {
  KarteUserDetailResponse,
  KarteResponse,
} from '@/apis/repositories/models/derek'
import { formatDiagnosisType } from '@/libs/utilities/formatDiagnosisResult'
import * as jisx0402 from '@/features/prefecture/jisx0402'
import type { DiagnosisByKarteQuery } from '@/pages/KarteListPage/hooks/useKarteUserDetail.generated.ts'
import { convertGqlGenderToSex } from '@/apis/repositories/models/superfrog.ts'

const hasPartner = (selectedValue: number): boolean => {
  return selectedValue === 0
}

const hasChild = (selectedValue: number): boolean => {
  return selectedValue === 0
}

const sexOfChild = (selectedValue: number): Sex => {
  return selectedValue === 0 ? 'male' : 'female'
}

export const createKarteUserDetail = (
  user: KarteUserDetailResponse
): KarteUser => ({
  id: user.id,
  nickname: user.nickname,
  diagnosisResult: {
    personality: {
      type: formatDiagnosisType(user.personality.id),
      title: user.personality.title,
      description: user.personality.description,
    },
  },
  yearOfBirth: user.yearOfBirth,
  sex: user.sex,
  livingPrefecture: jisx0402.findByCode(user.livingPrefecture)?.name ?? '',
  address: {
    prefecture: user.address?.prefecture ?? undefined,
    city: user.address?.city ?? undefined,
  },
  karte: createKarte(user.karte),
  annualIncome: user.annualIncome,
  partnerAnnualIncome: user.partnerAnnualIncome ?? undefined,
  saving: user.saving,
  investment: user.investment,
  monthlyInsurance: user.monthlyInsurance,
  hasPartner: hasPartner(user.partner),
  hasChild: hasChild(user.hasChild),
  children: user.children.map(value => ({
    age: value.age,
    sex: sexOfChild(value.sex),
  })),
  // FIXME: カルテ詳細のレスポンスに含まれるようになったら反映させるようにする
  isHighMatchingPotential: false,
  numberOfMatch: 0,
})

export const parseDiagnosisByKarteResponseToUser = (
  diagnosisByKarteQuery: DiagnosisByKarteQuery
): KarteUser => {
  const user = diagnosisByKarteQuery.diagnosisByKarte.user

  return {
    id: parseInt(user.id),
    diagnosisResult: {
      personality: {
        type: user.personality.type,
        title: user.personality.title,
        description: user.personality.description,
      },
    },
    nickname: user.nickname,
    yearOfBirth: user.yearOfBirth,
    sex: convertGqlGenderToSex(user.gender),
    livingPrefecture: user.livingPrefecture,
    annualIncome: user.annualIncome,
    partnerAnnualIncome: user.partnerAnnualIncome ?? undefined,
    saving: user.saving,
    investment: user.investment,
    monthlyInsurance: user.monthlyInsurance,
    hasPartner: user.hasPartner,
    hasChild: user.hasChild,
    children: user.children.map(child => ({
      age: new Date().getFullYear() - new Date(child.birthday).getFullYear(),
      sex: convertGqlGenderToSex(child.gender),
    })),
    karte: {
      id: parseInt(user.karte.id),
      plannerCommentCount: user.karte.plannerCommentCount,
      modifiedAt: new Date(user.karte.modifiedAt),
      channel: user.karte.channel,
      sameBranchPlannerCommented: user.karte.sameBranchPlannerCommented,
    },
  }
}

const createKarte = (karte: KarteResponse): Karte => ({
  id: karte.id,
  userComment: karte.userComment && {
    value: karte.userComment.value,
    noIdea: karte.userComment.noIdea,
  },
  plannerComment: karte.plannerComment && karte.plannerComment.value,
  plannerCommentCount: karte.plannerCommentCount,
  topics: karte.selectedTopics && {
    values: karte.selectedTopics.values,
    noIdea: karte.selectedTopics.noIdea,
  },
  sameBranchPlannerCommented: karte.sameBranchPlannerCommented,
  startedAt: new Date(karte.startedAt),
  modifiedAt: new Date(karte.modifiedAt),
  channel: convertLegacyChannelToGqlType(karte.channel.id),
})
